/**
 * <s-brand-rail>: one horizontally scrolling brand row on large collection pages.
 * Buttons target this element with commandfor + command="--prev" / "--next".
 * Expects one [data-track] scroll container and an optional [data-progress] bar inside.
 * Sets data-at-start / data-at-end on itself, disables the arrows at either end and moves the progress bar.
 */
class BrandRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.progress = this.querySelector('[data-progress]');
    this.buttons = [...this.querySelectorAll(`[commandfor="${this.id}"]`)];
    this.onCommand = (event) => {
      if (event.command !== '--prev' && event.command !== '--next') return;
      const direction = event.command === '--next' ? 1 : -1;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.85, behavior: reduce ? 'auto' : 'smooth' });
    };
    this.onScroll = () => this.update();
    this.addEventListener('command', this.onCommand);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.onCommand);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const left = Math.abs(scrollLeft);
    const atStart = left < 4;
    const atEnd = left + clientWidth >= scrollWidth - 4;
    this.toggleAttribute('data-at-start', atStart);
    this.toggleAttribute('data-at-end', atEnd);
    for (const button of this.buttons) {
      button.disabled = button.getAttribute('command') === '--prev' ? atStart : atEnd;
    }
    if (this.progress) {
      const ratio = Math.min(1, clientWidth / scrollWidth);
      this.progress.parentElement.hidden = ratio >= 1;
      this.progress.style.width = `${ratio * 100}%`;
      this.progress.style.transform = `translateX(${(left / scrollWidth / ratio) * 100}%)`;
    }
  }
}

if (!customElements.get('s-brand-rail')) {
  customElements.define('s-brand-rail', BrandRail);
}
